import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Trash2 } from "lucide-react";
import { UNIT_CODES } from "@/lib/romanianCounties";
import type { LineItem } from "./types";

interface LineItemsEditorProps {
  items: LineItem[];
  onAddItem: () => void;
  onRemoveItem: (id: string) => void;
  onUpdateItem: (id: string, field: keyof LineItem, value: string | number) => void;
}

const LineItemsEditor = ({ items, onAddItem, onRemoveItem, onUpdateItem }: LineItemsEditorProps) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-heading font-semibold">Produse / Servicii</h3>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="outline" size="sm" onClick={onAddItem}>
              <Plus className="h-4 w-4 mr-1" /> Adaugă
            </Button>
          </TooltipTrigger>
          <TooltipContent>Adaugă un produs sau serviciu nou</TooltipContent>
        </Tooltip>
      </div>
      {items.map((item, idx) => (
        <div key={item.id} className="rounded-xl border border-border bg-card p-4 space-y-3">
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">#{idx + 1}</span>
            {items.length > 1 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => onRemoveItem(item.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Șterge acest produs</TooltipContent>
              </Tooltip>
            )}
          </div>
          <div className="space-y-2">
            <Label>Denumire</Label>
            <Input
              placeholder="ex: Servicii consultanță"
              value={item.item_name}
              onChange={(e) => onUpdateItem(item.id, "item_name", e.target.value)}
            />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div className="space-y-2">
              <Label>Cantitate</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="1"
                value={item.quantity || ""}
                onChange={(e) => onUpdateItem(item.id, "quantity", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <Label>U.M.</Label>
              <Select value={item.unit_code} onValueChange={(v) => onUpdateItem(item.id, "unit_code", v)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {UNIT_CODES.map((u) => (
                    <SelectItem key={u.code} value={u.code}>{u.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Preț Unitar</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                placeholder="0"
                value={item.unit_price || ""}
                onChange={(e) => onUpdateItem(item.id, "unit_price", e.target.value === "" ? 0 : parseFloat(e.target.value) || 0)}
              />
            </div>
            <div className="space-y-2">
              <Label>TVA %</Label>
              <Input
                type="number"
                min="0"
                max="100"
                value={item.vat_rate}
                onChange={(e) => onUpdateItem(item.id, "vat_rate", parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>
          <div className="text-right text-sm text-muted-foreground">
            Subtotal: {(item.quantity * item.unit_price).toLocaleString("ro-RO", { style: "currency", currency: "RON" })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default LineItemsEditor;
